import { defineStore } from "pinia";

import type { MerchantJobStatusDto, MerchantOnboardingStateDto } from "./models.js";
import type { MerchantOnboardingService } from "./service.js";

export const useOnboardingStore = defineStore("merchant-onboarding", {
  state: (): {
    service?: MerchantOnboardingService;
    state?: MerchantOnboardingStateDto;
    activeJob?: MerchantJobStatusDto;
    loading: boolean;
    actionRunning: string | undefined;
    errorMessage?: string;
    pollTimer?: number;
  } => ({
    loading: false,
    actionRunning: undefined,
  }),
  getters: {
    isReadOnly: (state): boolean => state.state?.readOnly ?? true,
    shouldPoll: (state): boolean => {
      const status = state.activeJob?.status ?? state.state?.catalogSync.status;
      return status === "QUEUED" || status === "RUNNING";
    },
  },
  actions: {
    configure(service: MerchantOnboardingService): void {
      this.service = service;
    },
    async load(): Promise<void> {
      this.loading = true;
      delete this.errorMessage;
      try {
        this.applyOnboarding(await this.requireService().load());
      } catch (error: unknown) {
        this.errorMessage = messageForError(error);
      } finally {
        this.loading = false;
      }
    },
    async startCatalogSync(trigger: "MANUAL" | "RETRY"): Promise<void> {
      await this.runMutation(
        trigger === "RETRY" ? "retry_catalog_sync" : "start_catalog_sync",
        async () => {
          const result = await this.requireService().startCatalogSync(trigger);
          this.activeJob = result.job;
        },
      );
      await this.load();
    },
    async dismissGuidance(guidanceId: string): Promise<void> {
      await this.runMutation(actionForGuidance(guidanceId), async () => {
        const result = await this.requireService().runAction(actionForGuidance(guidanceId));
        this.applyOnboarding(result.onboarding);
      });
    },
    startPolling(): void {
      this.stopPolling();
      if (!this.shouldPoll) return;
      this.pollTimer = window.setTimeout(() => void this.pollOnce(), 3_000);
    },
    stopPolling(): void {
      if (this.pollTimer !== undefined) {
        window.clearTimeout(this.pollTimer);
        delete this.pollTimer;
      }
    },
    async pollOnce(): Promise<void> {
      const jobId = this.activeJob?.jobId ?? this.state?.catalogSync.jobId;
      if (jobId === undefined) return;

      try {
        this.activeJob = await this.requireService().loadJob(jobId);
        if (this.shouldPoll) {
          this.startPolling();
          return;
        }
        await this.load();
      } catch (error: unknown) {
        this.errorMessage = messageForError(error);
      }
    },
    async runMutation(action: string, mutation: () => Promise<void>): Promise<void> {
      if (this.isReadOnly) return;
      this.actionRunning = action;
      delete this.errorMessage;
      try {
        await mutation();
      } catch (error: unknown) {
        this.errorMessage = messageForError(error);
      } finally {
        this.actionRunning = undefined;
      }
    },
    applyOnboarding(nextState: MerchantOnboardingStateDto): void {
      this.state = nextState;
      if (nextState.catalogSync.jobId !== undefined) {
        this.activeJob = {
          jobId: nextState.catalogSync.jobId,
          jobType: "CATALOG_SYNC",
          status: toJobStatus(nextState.catalogSync.status),
          progressPercent: nextState.catalogSync.progressPercent,
          attemptCount: 0,
          maxAttempts: 3,
          createdAt: nextState.updatedAt,
          updatedAt: nextState.updatedAt,
          ...(nextState.catalogSync.errorCode === undefined
            ? {}
            : { errorCode: nextState.catalogSync.errorCode }),
          ...(nextState.catalogSync.errorSummary === undefined
            ? {}
            : { errorSummary: nextState.catalogSync.errorSummary }),
        };
      }
      this.startPolling();
    },
    requireService(): MerchantOnboardingService {
      if (this.service === undefined) {
        throw new Error("Onboarding store was not configured before use.");
      }
      return this.service;
    },
  },
});

function actionForGuidance(guidanceId: string): string {
  if (guidanceId === "competitors") return "dismiss_competitor_setup";
  if (guidanceId === "policy") return "dismiss_policy_setup";
  if (guidanceId === "widget") return "dismiss_widget_setup";
  return "mark_theme_activation_guidance_seen";
}

function toJobStatus(
  status: MerchantOnboardingStateDto["catalogSync"]["status"],
): MerchantJobStatusDto["status"] {
  if (status === "COMPLETE") return "SUCCEEDED";
  if (status === "NOT_STARTED") return "QUEUED";
  return status;
}

function messageForError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Onboarding data could not be loaded. Try again.";
}
